/**
 * Time formatting utilities for the application
 */

/**
 * Convert 24-hour time format (HH:MM) to 12-hour format with AM/PM
 * @param {string} time24 - Time in 24-hour format (e.g., "14:30")
 * @returns {string} Time in 12-hour format (e.g., "2:30 PM")
 */
export const formatTime12Hour = (time24) => {
  if (!time24) return '';
  
  const [hours, minutes] = time24.split(':').map(Number);
  
  if (isNaN(hours) || isNaN(minutes)) return time24;
  
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12; // Convert 0 to 12 for midnight
  
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

/**
 * Format date string (YYYY-MM-DD) to readable format WITHOUT timezone conversion
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {string} Formatted date (e.g., "Fri, Apr 10, 2026")
 */
export const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  
  const [year, month, day] = dateStr.split('-').map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return dateStr;
  
  // Create date at noon local time to avoid timezone issues
  const date = new Date(year, month - 1, day, 12, 0, 0);
  
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Format a Date object to 12-hour time string
 * @param {Date} date - Date object
 * @returns {string} Time in 12-hour format
 */
export const formatDateTo12Hour = (date) => {
  if (!(date instanceof Date) || isNaN(date)) return '';
  
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Generate time slots in 12-hour format
 * @param {number} startHour - Starting hour (0-23)
 * @param {number} endHour - Ending hour (0-23)
 * @param {number} intervalMinutes - Interval between slots
 * @returns {Array<{value: string, label: string}>} Array of time slot objects
 */
export const generateTimeSlots12Hour = (startHour = 9, endHour = 19, intervalMinutes = 30) => {
  const slots = [];
  
  for (let hour = startHour; hour < endHour; hour++) {
    for (let minute = 0; minute < 60; minute += intervalMinutes) {
      const time24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const time12 = formatTime12Hour(time24);
      
      slots.push({
        value: time24, // Store as 24-hour for backend
        label: time12  // Display as 12-hour for user
      });
    }
  }
  
  return slots;
};
